import { AxiosResponse } from 'axios';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';
import { DateTime } from 'luxon';
import { RequestAssessmentVM } from 'practice/components/detail/form/accept-messi/form';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { TypePractice } from 'practice/constants/type-practice';
import {
  AccordionPracticeVM,
  AccordionApplicantVM,
  BodyExceptionListVM,
  AccordionAccountholderVM,
  BodyPracticeStateVM,
  AccordionContactsVM,
  BodyInterestedPeopleVM,
  AccordionAttachmentsVM,
  AccordionAddressVM,
  ItalianAddressVM,
  ForeignAddressVM,
  PracticeDetailVM,
  AccordionFamilyVM,
  AccordionCohabitingVM,
  EndPracticeVM,
  MovementTypeVM,
} from 'practice/view-models/practice-detail';
import { PracticeEditDocumentVM } from 'practice/view-models/practice-edit-document';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import {
  PersonData,
  AddressData,
  ApplicantData,
  AttachmentsData,
  ContactData,
  ExeptionData,
  FamilyData,
  CohabitingData,
  ForeignAddress,
  InterestedPeopleData,
  ItalianAddress,
  PracticeData,
  StateData,
  SuspensionData,
  MovementTypeData,
} from 'shell/models/practice';

export const tipoCambioResidenza = (value: number | undefined): string => {
  switch (value) {
    case 0:
    case 1:
      return ' - nuova famiglia';
    case 2:
      return ' - aggregazione in famiglia';
    case 3:
      return ' - aggregazione in convivenza';
    default:
      return '';
  }
};

export const transformerPraticeAccordionDetail = (
  data: PracticeData
): AccordionPracticeVM | undefined => {
  if (!data) {
    return undefined;
  }

  let progressivoPratica = `${data.key?.progressivoPratica}`;

  if ((data.key?.progressivoPratica?.toString() || '').length < 6) {
    progressivoPratica = `000000${data.key?.progressivoPratica}`;

    const start = progressivoPratica.length - 6;

    progressivoPratica = progressivoPratica.substring(start);
  }

  const practice: AccordionPracticeVM = {
    tipoCasoPratica: data?.tipoCasoPratica?.toString(),
    numberPractice: `${data.key?.tipoRegistro || ''}-${
      data.key?.idAnnoPratica || ''
    }-${progressivoPratica}`,
    dateInception: data.dataDecorrenza ? DateTime.fromISO(data.dataDecorrenza) : null,
    state: data.statoPraticaVO?.descrizioneStatoPratica || '',
    codState: data.statoPraticaVO?.key?.codiceStatoPratica || '',
    lastOperation: data.tipoOperazioneVO?.descrizioneBreveTipoOperazione || '',
    numberPracticeAnpr: data.numRichiestaAnpr || undefined,
    typeInitiativeDescription: data.dominioPraticaVO?.testoValore || '',
    notaOperazioneEmigrazioneN010:
      data.operazioneVOList?.find((item) => item?.tipoOperazione === '80')?.noteOperazione || '',
    typePractice: data.tipoPraticaVO?.descrizioneBreveTipoPratica
      ? `${data.tipoPraticaVO?.descrizioneBreveTipoPratica}${tipoCambioResidenza(
          data?.tipoCasoPratica
        )}`
      : '',
    operations: data.operazioneVOList,
    cdCityProvDest: data.codComuneProvDest || '',
    tipoOperazione: data.tipoOperazioneVO?.key?.tipoOperazione || '',
    codiceStatoPratica: data.codiceStatoPratica || '',
    flagApplicant: data.flagRichiedente,
    tipoIniziativa: data.tipoIniziativa || '',
    reasonForPraticeNotAvailable:
      data.operazioneVOList?.find((item) => item?.tipoOperazione === '91')?.noteOperazione || '',
    dateOpen: data.timestampAperturaPratica
      ? DateTime.fromISO(data.timestampAperturaPratica)
      : undefined,
    dateClose: data.timestampChiusuraPratica
      ? DateTime.fromISO(data.timestampChiusuraPratica)
      : undefined,
  };

  return practice;
};

const transformRequestAssessment = (data: PracticeData): RequestAssessmentVM | undefined => {
  if (!data) {
    return undefined;
  }

  const request: RequestAssessmentVM = {
    //request: data.tipoUltimaOperazione === '06',
    request: true,
    note: data.operazioneVOList
      ? data.operazioneVOList[data.operazioneVOList.length - 1]?.note || ''
      : '',
    tipoUltimaOperazione: data.tipoUltimaOperazione || '',
  };

  return request;
};

const transformMovementType = (data: MovementTypeData | undefined): MovementTypeVM | undefined => {
  if (!data) {
    return undefined;
  }

  const request: MovementTypeVM = {
    pec: data.pec,
    telephone: data.numeroTelefono,
    peo: data.peo,
  };

  return request;
};

const transformerApplicantDetail = (
  data: ApplicantData | undefined
): AccordionApplicantVM | undefined => {
  if (!data) {
    return undefined;
  }

  const applicant: AccordionApplicantVM = {
    fiscalCode: data?.codFiscaleRichiedente || '',
    // isAire: data?.flagAire === '1', non presente in tabella
    name: data?.nome || '',
    withoutName: !Boolean(data?.nome),
    surname: data?.cognome || '',
    withoutSurname: !Boolean(data?.cognome),
    //for Recap
    birthDate: data.dataNascita ? DateTime.fromISO(data.dataNascita) : null,
    descriptionBirthCity: data.descrizioneComuneNascita || '',
    cdBirthCity: data.codComuneNascita?.toString() || '',
    sex: data.sesso || '',
    dateReleaseDocument: data.dataRilascioDocumento
      ? DateTime.fromISO(data.dataRilascioDocumento)
      : null,
    isForeignDocument: !!data.luogoEsteroRilascioDocumento
      ? DocumentRadioCertificate.Other
      : DocumentRadioCertificate.IdentityCard,
    numberDocument: data.numeroDocumento || '',
    municipalityDocument: data.comuneRilascioDocumento
      ? {
          description: data.nomeComuneRilascioDocumento || '',
          province: data.provinciaRilascioDocumento || '',
        }
      : null,
    consulateCode: data.luogoEsteroRilascioDocumento,
    // civilState: data.codStatoCivile || '', non presenti in tabella
    // nationality: data?.codCittadinanza || '', non presenti in tabella
    idANPR: data?.idAnprRichiedente || '',
    // cdParent: data?.codAnprParentela || '', non presenti in tabella
  };

  return applicant;
};

export const transformerExeptionDetail = (
  data: Array<ExeptionData> | undefined
): Array<BodyExceptionListVM> | undefined => {
  if (!data) {
    return undefined;
  }

  const exeptionList: Array<BodyExceptionListVM> = (data || []).map((item) => ({
    exceptionDetail: item.descrizioneNote || '',
    exceptionDate: item.timestampComunicazione
      ? DateTime.fromISO(item.timestampComunicazione).toFormat('dd/MM/yyyy HH:mm')
      : '',
    tipoMessaggio: item.tipoMessaggio || 'INFO',
    idEccezione: item.key?.idEccezione,
    codMessaggio: item.codMessaggio?.toString(),
    eccezioneIgnorabile: item.flagIgnoraErrore === '1',
  }));

  return exeptionList;
};

const transformerAccountholderDetail = (
  data: PersonData | undefined
): AccordionAccountholderVM | undefined => {
  if (!data) {
    return undefined;
  }

  const accountholder: AccordionAccountholderVM = {
    fiscalCode: data?.codFiscale || '',
    isAire: data?.flagAire === '1',
    name: data?.nome || '',
    withoutName: !Boolean(data?.nome),
    surname: data?.cognome || '',
    withoutSurname: !Boolean(data?.cognome),
    // releaseDocumentDate: data.dataRilascioDocumento
    //   ? DateTime.fromISO(data.dataRilascioDocumento)
    //   : null,
    // locationDocument: data.comuneRilascioDocumento || data.luogoEsteroRilascioDocumento || '',
    // numberDocument: data.numeroDocumento || '',
  };

  return accountholder;
};

const transformerStateDetail = (
  data: Array<StateData> | undefined
): Array<BodyPracticeStateVM> | undefined => {
  if (!data) {
    return undefined;
  }

  const stateList: Array<BodyPracticeStateVM> = (data || []).map((item, index) => ({
    nr: index + 1,
    date: item.tipoOperazioneVO?.timestampInserimento
      ? DateTime.fromISO(item.tipoOperazioneVO?.timestampInserimento).toFormat('dd/MM/yyyy HH.mm')
      : '',
    operation: item.tipoOperazioneVO?.descrizioneBreveTipoOperazione || '',
    cancellation: item.flagCancellazione === '1' ? 'SI' : 'NO',
    dateOperation: item.timestampOperazione
      ? DateTime.fromISO(item.timestampOperazione).toFormat('dd/MM/yyyy HH.mm')
      : '',
    operator: item.nomeOperatore || '',
    note: item.noteOperazione || '',
    service: item.codiceServizio || '',
  }));

  return stateList;
};

const transformerContactsDetail = (
  data: ContactData | undefined
): AccordionContactsVM | undefined => {
  if (!data) {
    return undefined;
  }

  const contacts: AccordionContactsVM = {
    telephone: data?.numTelefono ? Number.parseInt(data.numTelefono, 10) : undefined,
    mobilePhone: data?.numCellulare ? Number.parseInt(data.numCellulare, 10) : undefined,
    email: data?.email || '',
    pec: data?.pec || '',
    note: data?.noteContatto || '',
  };

  return contacts;
};

const transformerAttachmentsDetail = (
  data: Array<AttachmentsData> | undefined
): AccordionAttachmentsVM | undefined => {
  if (!data) {
    return undefined;
  }

  const files: Array<TypeFile> = (data || []).map((item) => {
    const file = new File(
      item.contenutoAllegato ? [item.contenutoAllegato] : [],
      item.nomeFile || ''
    );
    return {
      ...file,
      raw: (item.contenutoAllegato as string)
        ? `data:application/pdf;base64,${item.contenutoAllegato}`
        : undefined,
      name: item.nomeFile || '',
      idAllegato: item.key?.idAllegato,
    };
  });

  const descriptions: Array<string> = (data || []).map((item) => item.descrizioneFile || '');

  const attachments: AccordionAttachmentsVM = {
    files,
    //isNotDocument: !(files?.length > 0),
    descriptions,
  };

  return attachments;
};

export const transformerAttachmentsEdit = (
  response: AxiosResponse<HttpResponseList<AttachmentsData>>
): PracticeEditDocumentVM => {
  const data: Array<AttachmentsData> | undefined = response.data.payload?.list || undefined;

  return (transformerAttachmentsDetail(data) || {}) as PracticeEditDocumentVM;
};

const transformerInterestedPeopleDetail = (
  data: Array<InterestedPeopleData> | undefined
): Array<BodyInterestedPeopleVM> | undefined => {
  if (!data) {
    return undefined;
  }

  const interestedPeople: Array<BodyInterestedPeopleVM> = (data || []).map((item) => ({
    fiscalCode: item.codFiscale || '',
    personItem: item,
    surname: item.cognome || '',
    name: item.nome || '',
    aggregateToPractice: item?.posizionePersona !== 'X' ? 'S' : '',
    gender: item.sesso || '',
    dateBirth: item.dataNascita ? DateTime.fromISO(item.dataNascita).toFormat('dd/MM/yyyy') : '',
    placeBirth: item.comuneVO?.nome || item?.descrizioneComuneNascita || '',
    relationship: item.relazioneParentelaVO?.descrizione || '',
    licenseValue: item.flagPatente === '1' ? 'S' : 'N',
    carValue: item.flagVeicoli === '1' ? 'S' : 'N',
    aire: item.flagAire === '1' ? 'S' : 'N',
    // only accordion person
    accountholder: item.flagIntestatario === '1',
    provinceBirth: item.comuneVO?.provinciaVO?.sigla || undefined,
    stateBirth: item.comuneVO?.statoEsteroVO?.nome || undefined,
    //for Recap
    civilState: item.codStatoCivile || '',
    nationality: item.codCittadinanza || '',
    idANPR: item.key?.idAnprPersona || '',
    sipoSerialNumber: item.key?.idDbLocalePersona || undefined,
    cdParent: item.codAnprParentela || '',
    residenceOrigin: {
      irreperibile: item?.irreperibile,
      neverRegistered: item?.minorePreadozione,
      isForeign: !!item?.localita,
      origin: item?.comune
        ? {
            id: item?.comune?.codiceIstat || '',
            description: item?.comune?.nomeComune || '',
            province: item?.comune?.siglaProvinciaIstat || '',
          }
        : undefined,
      originState: item?.localita
        ? {
            id: item?.localita?.codiceStato || '',
            value: item?.localita?.descrizioneStato || '',
            province: item?.localita?.provinciaContea || '',
          }
        : undefined,
      originMunicipalityForeign: item?.localita
        ? {
            id: item?.localita?.codiceStato || '',
            description: item?.localita?.descrizioneLocalita || '',
            province: item?.localita?.provinciaContea || '',
          }
        : undefined,
      previousOrigin: item?.comunePrecedente?.descrizione,
      previousOriginProvince: item?.comunePrecedente?.provincia,
      previousOriginSipoCode: item?.comunePrecedente?.codiceSipo,
      previousOriginCode: item?.comunePrecedente?.codice,
    },
  }));

  return interestedPeople;
};

const transformerAddressDetail = (data: AddressData): AccordionAddressVM => {
  const dataItalian: ItalianAddress | undefined = data.italian;

  const italianAddress: ItalianAddressVM = {
    municipality: dataItalian?.citta || '',
    province: dataItalian?.provincia || '',
    street: dataItalian?.nomeVia || '',
    civicNumber: dataItalian?.numeroCivico?.toString() || '',
    exponent: dataItalian?.esponente || '',
    cap: dataItalian?.cap || '',
    codVia: dataItalian?.codVia?.toString() || '',
    codeStreet: dataItalian?.descrizioneSpecieToponimo || '',
    note: dataItalian?.notaOperatore || '',
    isHomeless: dataItalian?.flagIgnotaDimora === '1',
    codTipoVia: dataItalian?.codTipoVia || '',
    occupantiLegittimi: dataItalian?.tipoOccupanteLegittimo?.toString() || '',
    assensoOspitante: dataItalian?.flagConsensoOspitante === '1',
    effettuatoControlloSiatel: dataItalian?.flagControlloSiatel === '1',
    codSerieAe: dataItalian?.codSerieAe?.toString() || '',

    // Dati proprietario
    fiscalCodeProprietario: dataItalian?.codFiscaleProprietario || '',
    surnameProprietario: dataItalian?.cognomeProprietario || '',
    nameProprietario: dataItalian?.nomeProprietario || '',
    municipalityResidenceProprietario: dataItalian?.comuneProprietario || '',
    addressProprietario: dataItalian?.descIndirizzoProprietario || '',

    // Dati ospitante
    fiscalCodeOspitante: dataItalian?.codFiscaleOspitante || '',
    surnameOspitante: dataItalian?.cognomeOspitante || '',
    nameOspitante: dataItalian?.nomeOspitante || '',
    birthDateOspitante: dataItalian?.dataNascitaOspitante
      ? DateTime.fromISO(dataItalian.dataNascitaOspitante)
      : null,
    municipalityResidenceOspitante:
      dataItalian?.comuneResidenzaOspitante || dataItalian?.comuneResidenzaEnteOspitante || '',
    addressOspitante: dataItalian?.indirizzoOspitante || '',

    // Dati ERP
    fiscalCodeERP: dataItalian?.codFiscaleErp || '',
    enteERP: dataItalian?.descrizioneEnteErp || '',
    pecEnte: dataItalian?.descrizionePecEnte || '',

    // Dati struttura
    fiscalCodeStruttura: dataItalian?.codFiscaleEnteOspitante || '',
    nameStruttura: dataItalian?.nomeEnteOspitante || '',
    municipalityResidenceStruttura: dataItalian?.comuneResidenzaEnte || '',
    addressStruttura: dataItalian?.indirizzoEnteOspitante || '',
    pecEnteStruttura: dataItalian?.descrizionePecEnte || '',

    // Dati catastali
    section: dataItalian?.sezione || '',
    houseOccupationType: dataItalian?.tipoOccupazione || '',
    houseOccupationDecType: dataItalian?.descTipoOccupazione || '',
    sheet: dataItalian?.foglio || '',
    particle: dataItalian?.particellaMappale || '',
    sub: dataItalian?.subalterno || '',
    cat: dataItalian?.categoriaCatastale || '',
    withDatiCatastali: Boolean(
      dataItalian?.sezione ||
        dataItalian?.foglio ||
        dataItalian?.particellaMappale ||
        dataItalian?.subalterno ||
        dataItalian?.categoriaCatastale
    ),
    // Dati agenzia delle entrate
    site: dataItalian?.sedeAgenziaEntrate?.toString() || '',
    number: dataItalian?.numeroRegistrazioneAgenziaEntrate || '',
    date: dataItalian?.dataRegistrazioneAgenziaEntrate
      ? DateTime.fromISO(dataItalian?.dataRegistrazioneAgenziaEntrate)
      : null,
    withDatiAgenzia: Boolean(
      dataItalian?.sedeAgenziaEntrate ||
        dataItalian?.numeroRegistrazioneAgenziaEntrate ||
        dataItalian?.dataRegistrazioneAgenziaEntrate
    ),
  };

  const dataForeign: ForeignAddress | undefined = data.foreign;

  const foreignAddress: ForeignAddressVM = {
    foreignState: dataForeign?.statoEstero || '',
    foreignStateId: dataForeign?.codNazione || '',
    foreignMunicipality: dataForeign?.comune || '',
    foreignProvince: dataForeign?.contea || '',
    cap: dataForeign?.cap || '',
    toponymDenomination: dataForeign?.indirizzo || '',
    civic: dataForeign?.civico?.toString() || '',
    consulate: dataForeign?.nomeConsolato || '',
    territory: dataForeign?.nomeTerritorioConsolato || '',
    foreignMunicipalityId: dataForeign?.codComune?.toString() || '',
  };

  const address: AccordionAddressVM = {
    italianAddress,
    foreignAddress,
    isItalian: Boolean(
      italianAddress?.municipality ||
        italianAddress?.street ||
        italianAddress?.civicNumber ||
        italianAddress?.exponent ||
        italianAddress?.cap ||
        italianAddress?.codeStreet ||
        italianAddress?.province ||
        italianAddress?.withDatiCatastali ||
        italianAddress?.site ||
        italianAddress?.number ||
        italianAddress?.date
    ),
  };

  return address;
};

const transformerFamilyDetail = (data: FamilyData | undefined): AccordionFamilyVM | undefined => {
  if (!data) {
    return undefined;
  }
  const family: AccordionFamilyVM = {
    idAnprFamily: data.idAnprFamiglia || '',
  };
  return family;
};

const transformerCohabitingDetail = (
  data: CohabitingData | undefined
): AccordionCohabitingVM | undefined => {
  if (!data) {
    return undefined;
  }
  const cohabiting: AccordionCohabitingVM = {
    idAnprCohabiting: data.idAnprConvivenza || '',
  };
  return cohabiting;
};

const transformerSuspensionDetail = (
  data: SuspensionData | undefined
): EndPracticeVM | undefined => {
  if (!data) {
    return undefined;
  }
  const suspension: EndPracticeVM = {
    startSuspensionDate: data.timestampInizioSospensione
      ? DateTime.fromISO(data.timestampInizioSospensione)
      : null,
    endSuspensionDate: data.timestampFineSospensione
      ? DateTime.fromISO(data.timestampFineSospensione)
      : DateTime.now(),
    timeStart: data.timestampInizioSospensione
      ? DateTime.fromISO(data.timestampInizioSospensione)
      : null,
    timeEnd: data.timestampFineSospensione
      ? DateTime.fromISO(data.timestampFineSospensione)
      : DateTime.now(),
    //reason: data.noteSospensione || '',
  };
  return suspension;
};

export const transformerPraticeDetail = (
  response: AxiosResponse<HttpResponse<PracticeData>>
): PracticeDetailVM => {
  const data = response.data.payload || {};

  const practice = transformerPraticeAccordionDetail(data);

  const applicant = transformerApplicantDetail(data.richiedenteVO);

  const accountholder = transformerAccountholderDetail(data.personaVO);

  const exceptionList = transformerExeptionDetail(data.eccezioneVO);

  const contacts = transformerContactsDetail(data.contattoVO);

  const attachments = transformerAttachmentsDetail(data.allegatoVOList);

  const practiceState = transformerStateDetail(data.operazioneVOList);

  const interestedPeople = transformerInterestedPeopleDetail(data.personaVOList);

  const family = transformerFamilyDetail(data.famigliaVO);

  const cohabiting = transformerCohabitingDetail(data.convivenzaVO);

  const suspension = transformerSuspensionDetail(data.sospensioneVO);

  const address = transformerAddressDetail({
    italian: data.indirizzoVO,
    foreign: data.indirizzoEsteroVO,
  } as AddressData);

  const requestAssessment = transformRequestAssessment(data);

  const managerCode = data.codResponsabile;

  const movementType = transformMovementType(data.tipoMovimentoVO);

  // dataProtocolloProvenienza,
  // progressivoEnteProvenienza, // Da recuperare
  // controllare tipoOperazione 00
  // dentro ad OperazioneVOList
  const subscriptionAire = (data.operazioneVOList || [])?.find((item) => {
    return item.tipoOperazione === '00';
  });

  return {
    practice,
    applicant,
    exceptionList: { body: (exceptionList || []) as Array<BodyExceptionListVM> },
    accountholder,
    contacts,
    attachments,
    practiceState: { body: (practiceState || []) as Array<BodyPracticeStateVM> },
    interestedPeople: { body: (interestedPeople || []) as Array<BodyInterestedPeopleVM> },
    address,
    family,
    cohabiting,
    suspension,
    managerCode,
    movementType,
    requestAssessment,
    noteAnnullement: data.notaAnnullamento || undefined,
    subscriptionAire:
      subscriptionAire &&
      (data.tipoPratica === TypePractice.Emigration ||
        data.tipoPratica === TypePractice.EmigrationC01)
        ? {
            number: subscriptionAire.progressivoEnteProvenienza,
            date: subscriptionAire.dataProtocolloProvenienza
              ? DateTime.fromISO(subscriptionAire.dataProtocolloProvenienza)
              : null,
          }
        : undefined,
  };
};
